import type { SurfaceDragArea } from "@project/emerge-types";
import type * as THREE from "three";

export type SurfaceAxis = "x" | "y" | "z";

/** One candidate panel of a side before padding: its inner rect and center. */
export interface PaddableSurfaceArea {
  rectMin: THREE.Vector3;
  rectMax: THREE.Vector3;
  rectCenter: THREE.Vector3;
}

/**
 * Shrinks one panel inward by the given in-plane paddings. Works regardless of
 * whether min/max are ordered along each axis (sides differ in direction).
 *
 * An axis whose panel extent is smaller than twice its padding is *over-padded*:
 * the part does not fit on the panel along that axis and the shrunk range would
 * come out inverted (min past max). Such a range is not a smaller area but no
 * area at all — drawn as a box it reads as an oversized drop zone hanging over
 * the panel, and clamped into it puts the part where it cannot be mounted.
 */
export function padSurfaceArea(
  area: PaddableSurfaceArea,
  horizontalAxis: SurfaceAxis,
  verticalAxis: SurfaceAxis,
  horizontalPadding: number,
  verticalPadding: number,
): {
  bounds: { min: THREE.Vector3; max: THREE.Vector3 };
  overPaddedAxes: SurfaceAxis[];
} {
  const min = area.rectMin.clone();
  const max = area.rectMax.clone();
  const overPaddedAxes: SurfaceAxis[] = [];

  for (const [axis, padding] of [
    [horizontalAxis, horizontalPadding],
    [verticalAxis, verticalPadding],
  ] as const) {
    const extent = Math.abs(max[axis] - min[axis]);
    if (extent < 2 * padding) {
      overPaddedAxes.push(axis);
    }
    if (min[axis] < max[axis]) {
      min[axis] += padding;
      max[axis] -= padding;
    } else {
      min[axis] -= padding;
      max[axis] += padding;
    }
  }

  return { bounds: { min, max }, overPaddedAxes };
}

/**
 * Pads every candidate panel and keeps only those the part fits on.
 *
 * A panel the part does not fit on is dropped: it is not a drop zone, so it
 * gets no outline, no center snap, and no share of the movement bounds. When
 * the part fits on none of the panels (larger than every panel of the side)
 * the primary panel — `areas[0]`, the one the part was resolved on — is kept
 * with each over-padded axis collapsed to the panel center, so the constraint
 * still has well-defined bounds and the part can be dragged along the axis it
 * does fit on and centered on the one it does not.
 */
export function padSurfaceDragAreas(
  areas: PaddableSurfaceArea[],
  horizontalAxis: SurfaceAxis,
  verticalAxis: SurfaceAxis,
  horizontalPadding: number,
  verticalPadding: number,
): SurfaceDragArea[] {
  const padded = areas.map((area) => ({
    area,
    ...padSurfaceArea(
      area,
      horizontalAxis,
      verticalAxis,
      horizontalPadding,
      verticalPadding,
    ),
  }));

  const fitting = padded.filter((entry) => entry.overPaddedAxes.length === 0);
  const kept = fitting.length > 0 ? fitting : padded.slice(0, 1);

  return kept.map(({ area, bounds, overPaddedAxes }) => {
    for (const axis of overPaddedAxes) {
      bounds.min[axis] = area.rectCenter[axis];
      bounds.max[axis] = area.rectCenter[axis];
    }
    return {
      bounds: {
        min: [bounds.min.x, bounds.min.y, bounds.min.z],
        max: [bounds.max.x, bounds.max.y, bounds.max.z],
      },
      center: [area.rectCenter.x, area.rectCenter.y, area.rectCenter.z],
    };
  });
}
